import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { 
  resolveSupportAppId, 
  createSupportTicket, 
  fetchSupportTicketById,
  createSupportLog 
} from '../../services/support';
import { 
  SUPPORT_APP_NAME, 
  SUPPORT_ENVIRONMENT, 
  SUPPORT_APP_VERSION 
} from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiLifeBuoy, FiSend, FiAlertCircle, FiCheckCircle, 
  FiPaperclip, FiX, FiLoader, FiCopy
} = FiIcons;

const TICKET_TYPES = [
  "Bug Report", 
  "Feature Request", 
  "Billing", 
  "How To", 
  "Other"
];

const SEVERITIES = [
  "Low", 
  "Medium", 
  "High", 
  "Critical"
];

const generateTicketRef = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  // Generate 4 random characters
  const random = Math.random().toString(36).substring(2, 6).toUpperCase().padEnd(4, 'X');
  return `BPAS-${yyyy}${mm}${dd}-${hh}${min}${ss}-${random}`;
};

const Support = () => {
  const { currentUserEmail, currentUser } = useAuth();
  
  const [appRecordId, setAppRecordId] = useState(null);
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successTicket, setSuccessTicket] = useState(null); // { ref: 'BPAS-2024...', id: '...' }
  const [copyFeedback, setCopyFeedback] = useState('');
  const fileInputRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    type: 'Bug Report',
    subject: '',
    description: '',
    severity: 'Low',
    requesterName: '',
    requesterEmail: ''
  });
  
  const [screenshots, setScreenshots] = useState([]);

  // Initialize
  useEffect(() => {
    const initSupport = async () => {
      try {
        const appId = await resolveSupportAppId();
        if (!appId) {
          setError("Support is not configured for this app. Please contact support manually.");
        } else {
          setAppRecordId(appId);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoadingApp(false);
      }
    };

    initSupport();

    // Pre-fill identity if available
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        requesterName: currentUser.display_name || '',
        requesterEmail: currentUser.email || currentUserEmail || ''
      }));
    }
  }, [currentUser, currentUserEmail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (screenshots.length + files.length > 5) {
      alert("You can only upload a maximum of 5 screenshots.");
      return;
    }
    setScreenshots(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = null; 
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appRecordId) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      // 1. Prepare Meta Data
      const clientMeta = {
        userAgent: window.navigator.userAgent,
        platform: window.navigator.platform,
        language: window.navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height
      };

      // 2. Generate Client-side Reference
      const clientRef = generateTicketRef();

      // 3. Build FormData
      const payload = new FormData();
      payload.append('app', appRecordId);
      payload.append('ticketRef', clientRef); // Include generated ref
      payload.append('type', formData.type);
      payload.append('subject', formData.subject);
      payload.append('description', formData.description);
      payload.append('status', 'New');
      payload.append('severity', formData.severity);
      payload.append('requesterName', formData.requesterName);
      payload.append('requesterEmail', formData.requesterEmail);
      payload.append('pageUrl', window.location.href);
      payload.append('appVersion', SUPPORT_APP_VERSION);
      payload.append('environment', SUPPORT_ENVIRONMENT);
      payload.append('clientMeta', JSON.stringify(clientMeta));

      // Append Screenshots
      screenshots.forEach(file => {
        payload.append('screenshots', file);
      });

      // 4. Create Ticket
      const ticket = await createSupportTicket(payload);
      const createdId = ticket.id;

      // 5. Create Log
      await createSupportLog(createdId, formData.requesterName, formData.requesterEmail);

      // 6. Verification & Final Reference
      let finalRef = clientRef;
      
      // Use ref returned by server if present immediately
      if (ticket.ticketRef) {
        finalRef = ticket.ticketRef;
      }

      // Double-check by fetching fresh record (as requested)
      if (createdId) {
        const verifiedTicket = await fetchSupportTicketById(createdId);
        if (verifiedTicket && verifiedTicket.ticketRef) {
          finalRef = verifiedTicket.ticketRef;
        }
      }

      // 7. Show Success
      setSuccessTicket({ ref: finalRef, id: createdId });
      setScreenshots([]);
      setFormData(prev => ({
        ...prev,
        subject: '',
        description: '',
        severity: 'Low'
      }));

    } catch (err) {
      setError("Failed to submit ticket: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyRef = async () => {
    if (!successTicket?.ref) return;
    try {
      await navigator.clipboard.writeText(successTicket.ref);
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
    }
  };

  const handleNewTicket = () => {
    setSuccessTicket(null);
    setError('');
  };

  if (isLoadingApp) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Blocking Error (App config missing)
  if (error && !appRecordId) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-red-800 mb-2">Support Unavailable</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <SafeIcon icon={FiLifeBuoy} className="w-6 h-6 mr-3 text-blue-600" />
            Support
          </h1>
        </div>

        {/* Success View */}
        {successTicket ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-lg p-8 text-center"
          >
            <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 mb-2">Ticket Submitted Successfully!</h2>
            <p className="text-green-700 mb-6">
              Thank you for contacting support. Your ticket has been created.
            </p>
            
            <div className="bg-white border border-green-200 rounded-md p-4 max-w-sm mx-auto mb-8 flex items-center justify-between shadow-sm">
              <div className="text-left">
                <p className="text-xs text-gray-500 uppercase font-semibold">Ticket Reference</p>
                <p className="text-xl font-mono font-bold text-gray-900">
                  {successTicket.ref || "REF-PENDING"}
                </p>
              </div>
              <button 
                onClick={handleCopyRef}
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                title="Copy Reference"
              >
                <SafeIcon icon={FiCopy} className="w-5 h-5" />
              </button>
            </div>
            {copyFeedback && <p className="text-sm text-green-600 -mt-6 mb-6">{copyFeedback}</p>}

            <button
              onClick={handleNewTicket}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium shadow-sm"
            >
              Submit Another Ticket
            </button>
          </motion.div>
        ) : (
          /* Form View */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
            <p className="text-gray-600 mb-8">
              Need help? Fill out the form below to submit a ticket directly to the {SUPPORT_APP_NAME} support team.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6 flex items-start">
                <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Identity Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-md border border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    name="requesterName"
                    value={formData.requesterName}
                    onChange={handleInputChange}
                    required
                    readOnly={!!currentUser}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${currentUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
                  <input
                    type="email"
                    name="requesterEmail"
                    value={formData.requesterEmail}
                    onChange={handleInputChange}
                    required
                    readOnly={!!currentUser}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${currentUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              {/* Ticket Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {TICKET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                  <select
                    name="severity"
                    value={formData.severity}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  placeholder="Brief summary of the issue"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  placeholder="Please describe the issue in detail..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Screenshots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Screenshots (Optional, max 5)
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    disabled={screenshots.length >= 5}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiPaperclip} className="w-4 h-4 mr-2" />
                    Attach Files
                  </button>
                  <span className="text-xs text-gray-500">
                    {screenshots.length} / 5 files selected
                  </span>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  accept="image/*"
                  className="hidden"
                />
                
                {/* File List */}
                {screenshots.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {screenshots.map((file, index) => (
                      <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 text-sm">
                        <span className="truncate max-w-xs">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeScreenshot(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <SafeIcon icon={FiX} className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-gray-100 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm font-medium"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Support;